// Rol şablonları — personel listesinin ikincil listesi; yan panel yerine
// sağdan açılan drawer (design-principles §3.5). Satır tıklanınca şablon
// editörü açılır; düzenleme/silme orada. Footer: Kapat + Yeni Rol.

import React from "react";
import { Box, ButtonBase, Drawer, IconButton, Skeleton, Tooltip, Typography, alpha } from "@mui/material";
import {
  Shield as ShieldIcon,
  ContentCopy as CopyIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { DrawerFooter, DrawerHeader } from "../../../../components/atoms";
import EmptyState from "../../../../components/atoms/EmptyState";
import { StatusPill } from "../../../../components/organisms/ClinicRecordList/recordTableParts";
import { COLORS, TYPO } from "../../../../styles/uiConstants";

const paperSx = {
  width: { xs: "100%", sm: 480 },
  bgcolor: COLORS.white,
  boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
  display: "flex",
  flexDirection: "column",
};

export default function RolesDrawer({
  open,
  onClose,
  templates,
  isLoading,
  staffCountOf,
  canCreate,
  canOpen,
  onCreate,
  onOpen,
  onCopyCode,
  palette,
}) {
  const createLink = (label) =>
    canCreate ? (
      <ButtonBase
        onClick={onCreate}
        sx={{
          font: "inherit",
          color: palette.primary,
          fontWeight: 600,
          textDecoration: "underline",
          textUnderlineOffset: 3,
          verticalAlign: "baseline",
        }}
      >
        {label}
      </ButtonBase>
    ) : null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: paperSx }}>
      <DrawerHeader
        icon={<ShieldIcon />}
        title="Rol Şablonları"
        subtitle={isLoading ? "Yükleniyor…" : `${templates.length} şablon · personele atanan izin setleri`}
        onClose={onClose}
      />

      <Box sx={{ px: 3, py: 1.5, bgcolor: COLORS.slate50, borderBottom: `1px solid ${COLORS.slate200}`, flexShrink: 0 }}>
        <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate600, lineHeight: 1.5 }}>
          Her şablon bir izin setidir. Personele rol atadığınızda izinleri bu şablondan gelir. Varsayılan
          şablon silinemez.
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {isLoading ? (
          [0, 1, 2, 3].map((i) => (
            <Box key={i} sx={{ px: 3, py: 1.5, display: "flex", alignItems: "center", gap: 1.5, borderBottom: `1px solid ${COLORS.slate100}` }}>
              <Skeleton variant="rounded" width={34} height={34} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="50%" />
                <Skeleton variant="text" width="70%" />
              </Box>
            </Box>
          ))
        ) : templates.length === 0 ? (
          <Box sx={{ pt: 4 }}>
            <EmptyState
              variant="illustrated-sm"
              icon={<ShieldIcon />}
              title="Henüz rol şablonu yok"
              description={
                canCreate ? (
                  <>Doktor, sekreter gibi rolleri {createLink("ilk şablonu oluşturarak")} tanımlayın.</>
                ) : (
                  "Bu klinikte tanımlı rol şablonu bulunmuyor."
                )
              }
            />
          </Box>
        ) : (
          templates.map((t) => {
            const count = staffCountOf(t._id);
            const meta = [`${(t.permissions || []).length} izin`, `${count} personel`].join(" · ");
            return (
              <Box
                key={t._id}
                role={canOpen ? "button" : undefined}
                tabIndex={canOpen ? 0 : undefined}
                onClick={canOpen ? () => onOpen(t) : undefined}
                onKeyDown={
                  canOpen
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onOpen(t);
                        }
                      }
                    : undefined
                }
                sx={{
                  px: 3,
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  borderBottom: `1px solid ${COLORS.slate100}`,
                  cursor: canOpen ? "pointer" : "default",
                  "&:hover": canOpen ? { bgcolor: alpha(palette.primary, 0.03) } : {},
                  "&:focus-visible": { outline: `2px solid ${alpha(palette.primary, 0.5)}`, outlineOffset: -2 },
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 2,
                    flexShrink: 0,
                    bgcolor: alpha(palette.primary, 0.08),
                    color: palette.primary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ShieldIcon sx={{ fontSize: 17 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }} noWrap>
                      {t.name}
                    </Typography>
                    {t.isDefault ? <StatusPill label="Varsayılan" tone="warning" /> : null}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                    <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }} noWrap>
                      {t.code ? (
                        <>
                          Kod{" "}
                          <Box component="span" sx={{ fontFamily: "'SF Mono', 'Fira Code', monospace", color: COLORS.slate700 }}>
                            {t.code}
                          </Box>
                          {" · "}
                        </>
                      ) : null}
                      {meta}
                    </Typography>
                    {t.code ? (
                      <Tooltip title="Kodu kopyala" arrow>
                        <IconButton
                          size="small"
                          aria-label="Kodu kopyala"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCopyCode(t.code);
                          }}
                          sx={{ width: 22, height: 22 }}
                        >
                          <CopyIcon sx={{ fontSize: 12, color: COLORS.slate400 }} />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                </Box>
                {canOpen ? <ChevronRightIcon sx={{ fontSize: 18, color: COLORS.slate300 }} /> : null}
              </Box>
            );
          })
        )}
      </Box>

      <DrawerFooter
        onCancel={onClose}
        cancelLabel="Kapat"
        onSubmit={canCreate ? onCreate : undefined}
        submitLabel="Yeni Rol"
        startIcon={<ShieldIcon sx={{ fontSize: 15 }} />}
        fullWidth
      />
    </Drawer>
  );
}
